class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    var self = this;
    
    this.slider = this.querySelector('.carousel-container');
    this.sliderItems = this.querySelectorAll('.slide');
    this.prevButton = this.querySelector('.carousel-prev');
    this.nextButton = this.querySelector('.carousel-next');
    this.pagination = this.querySelector('.carousel-pagination');

    // Exit early if required elements don't exist
    if (!this.slider) {
      console.warn('Carousel component requires a .carousel-container element');
      return;
    }

    this.isDragging = false;
    this.startPosition = 0;
    this.startScrollLeft = 0;
    this.currentAnimation = null;
    this.isMobile = window.innerWidth <= 767; // Assuming $screen-xs-max is 767px
    
    // Get initial slide from attribute or default to 0 (first slide)
    this.initialSlide = parseInt(this.getAttribute('initial-slide') || '0');
    
    // Store last known attribute values
    this._lastInitialSlide = this.getAttribute('initial-slide');
    this._lastAutoPlay = this.hasAttribute('auto-play');
    this._lastAutoPlayDelay = this.getAttribute('auto-play-delay');

    console.log("CarouselComponent", this.slider);

    // Add resize handler to detect mobile/desktop changes
    this._resizeHandler = function() {
      var wasMobile = self.isMobile;
      self.isMobile = window.innerWidth <= 767;
      
      // If switching between mobile and desktop, reinitialize
      if (wasMobile !== self.isMobile) {
        self.setupEventListeners();
        self.initPages();
      } else {
        self.initPages();
      }
    };
    
    window.addEventListener('resize', this._resizeHandler);

    var resizeObserver = new ResizeObserver(function(entries) {
      self.initPages();
    });
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.updateNavigation.bind(this));
    
    this.setupEventListeners();

    // Auto-play functionality
    this.autoPlayInterval = null;
    this.autoPlayDelay = parseInt(this.getAttribute('auto-play-delay')) || 5000;
    if (this.hasAttribute('auto-play')) {
      this.startAutoPlay();
      
      // Pause on hover
      this.slider.addEventListener('mouseenter', this.stopAutoPlay.bind(this));
      this.slider.addEventListener('mouseleave', this.startAutoPlay.bind(this));
    }
    
    // Set up a MutationObserver to watch for attribute changes
    this.attributeObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes') {
          self.updateFromAttributes();
        }
      });
    });
    
    this.attributeObserver.observe(this, { 
      attributes: true, 
      attributeFilter: ['initial-slide', 'auto-play', 'auto-play-delay'] 
    });

    this.initPages();
  }

  updateFromAttributes() {
    // Check for initial-slide
    var initialSlideAttr = this.getAttribute('initial-slide');
    if (initialSlideAttr !== null && initialSlideAttr !== this._lastInitialSlide) {
      this._lastInitialSlide = initialSlideAttr;
      this.initialSlide = parseInt(initialSlideAttr || '0');
      if (this._initialSlideSet) {
        this.setActiveSlide(this.initialSlide);
      }
    }
    
    // Check for auto-play
    var hasAutoPlay = this.hasAttribute('auto-play');
    if (hasAutoPlay !== this._lastAutoPlay) {
      this._lastAutoPlay = hasAutoPlay;
      if (hasAutoPlay) {
        this.startAutoPlay();
      } else {
        this.stopAutoPlay();
      }
    }
    
    // Check for auto-play-delay
    var autoPlayDelay = this.getAttribute('auto-play-delay');
    if (autoPlayDelay !== this._lastAutoPlayDelay) {
      this._lastAutoPlayDelay = autoPlayDelay;
      this.autoPlayDelay = parseInt(autoPlayDelay || '5000');
      if (this.hasAttribute('auto-play')) {
        this.stopAutoPlay();
        this.startAutoPlay();
      }
    }
  }

  connectedCallback() {
    // Set tabindex so the component can receive keyboard focus
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    
    // Expose a public method to jump to a specific slide
    var self = this;
    this.goToSlide = function(index) {
      self.setActiveSlide(index);
    };
  }

  disconnectedCallback() {
    // Clean up event listeners and intervals when component is removed
    this.stopAutoPlay();
    
    if (this.currentAnimation) {
      cancelAnimationFrame(this.currentAnimation);
    }
    
    // Clean up our attribute observer
    if (this.attributeObserver) {
      this.attributeObserver.disconnect();
    }
    
    // Clean up window resize event listener
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  setupEventListeners() {
    var self = this;
    
    // Remove existing listeners first to prevent duplicates
    if (this._onDragStart) {
      this.slider.removeEventListener('mousedown', this._onDragStart);
      this.slider.removeEventListener('mousemove', this._onDragMove);
      this.slider.removeEventListener('mouseup', this._onDragEnd);
      this.slider.removeEventListener('mouseleave', this._onDragEnd);
      this.slider.removeEventListener('touchstart', this._onDragStart);
      this.slider.removeEventListener('touchmove', this._onDragMove);
      this.slider.removeEventListener('touchend', this._onDragEnd);
    }
    
    if (this.prevButton) {
      this._onButtonClick = this.onButtonClick.bind(this);
      this.prevButton.addEventListener('click', this._onButtonClick);
    }
    if (this.nextButton) {
      if (!this._onButtonClick) {
        this._onButtonClick = this.onButtonClick.bind(this);
      }
      this.nextButton.addEventListener('click', this._onButtonClick);
    }

    // Add keyboard navigation
    this._onKeyDown = this.onKeyDown.bind(this);
    this.addEventListener('keydown', this._onKeyDown);
    
    // On desktop, add custom drag functionality
    if (!this.isMobile) {
      // Add desktop-only drag events
      this._onDragStart = this.onDragStart.bind(this);
      this._onDragMove = this.onDragMove.bind(this);
      this._onDragEnd = this.onDragEnd.bind(this);
      
      this.slider.addEventListener('mousedown', this._onDragStart);
      this.slider.addEventListener('mousemove', this._onDragMove);
      this.slider.addEventListener('mouseup', this._onDragEnd);
      this.slider.addEventListener('mouseleave', this._onDragEnd);
      
      // Add touch functionality (for tablets in desktop view)
      this.slider.addEventListener('touchstart', this._onDragStart);
      this.slider.addEventListener('touchmove', this._onDragMove);
      this.slider.addEventListener('touchend', this._onDragEnd);
    }
  }

  initPages() {
    var self = this;
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(function(element) {
      return element.clientWidth > 0;
    });
    
    if (this.sliderItemsToShow.length <= 1) return; // if there is only one child then exit;
    
    this.sliderItemWidth = this.sliderItemsToShow[0].getBoundingClientRect().width;
    
    // On mobile, respect the CSS settings (90% width + padding)
    if (this.isMobile) {
      // In mobile mode, we respect the native scroll-snap behavior set in CSS
      this.slidesPerPage = 1;
      this.totalPages = this.sliderItemsToShow.length;
    } else {
      // In desktop mode, calculate slidesPerPage based on container width
      this.slidesPerPage = Math.floor(this.slider.getBoundingClientRect().width / this.sliderItemWidth);
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    }
    
    // Ensure we don't have negative pages
    this.totalPages = Math.max(1, this.totalPages);
    
    // Update cursor style for desktop
    if (!this.isMobile) {
      this.slider.style.cursor = 'grab';
    } else {
      this.slider.style.cursor = '';
    }
    
    this.createBullets();
    this.updateNavigation();
    
    // Set initial slide position after a short delay to ensure layout is complete
    if (!this._initialSlideSet) {
      this._initialSlideSet = true;
      
      // Don't use any animation or scrollIntoView methods on mobile to prevent jumps
      if (this.isMobile) {
        console.log('Mobile detected: Not auto-scrolling carousel');
        
        // Update bullets to match initial slide value if needed, but don't scroll
        if (this.pagination && this.pagination.children.length > 0) {
          for (var i = 0; i < this.pagination.children.length; i++) {
            this.pagination.children[i].classList.remove('active');
          }
          
          // Use initialSlide for bullet highlighting but don't scroll
          var activeIndex = Math.min(this.initialSlide, this.pagination.children.length - 1);
          if (activeIndex >= 0) {
            this.pagination.children[activeIndex].classList.add('active');
          }
        }
      } else {
        // Only on desktop: Validate initial slide index
        if (this.initialSlide < 0) {
          this.initialSlide = 0;
        } else if (this.initialSlide >= this.totalPages) {
          this.initialSlide = this.totalPages - 1;
        }
        
        var initialSlide = this.initialSlide;
        
        // For desktop with non-zero initial slide: set position directly without animation
        if (initialSlide > 0) {
          // Use requestAnimationFrame to ensure we're in the next layout cycle
          requestAnimationFrame(function() {
            // Direct positioning without any scrolling methods that might affect page position
            self.slider.scrollLeft = initialSlide * self.sliderItemWidth;
            self.updateNavigation();
          });
        } else {
          // For slide 0 on desktop, just make sure navigation is updated
          this.updateNavigation();
        }
      }
    }
  }

  createBullets() {
    // Check if pagination element exists first
    if (!this.pagination) {
      return; // Exit if pagination element doesn't exist
    }
    
    var self = this;
    this.pagination.innerHTML = '';
    if (this.totalPages > 1) {
      for (var i = 0; i < this.totalPages; i++) {
        (function(index) {
          var bullet = document.createElement('span');
          bullet.classList.add('carousel-bullet');
          bullet.setAttribute('aria-label', 'Go to slide ' + (index + 1));
          bullet.addEventListener('click', function() {
            self.setActiveSlide(index);
          });
          self.pagination.appendChild(bullet);
        })(i);
      }
    }
  }

  updateNavigation() {
    // Calculate current page differently based on device type
    if (this.isMobile) {
      // In mobile, use scroll-snap-align to determine the current page
      // Find which slide is closest to the left edge
      var scrollLeft = this.slider.scrollLeft;
      var minDistance = Infinity;
      var closestIndex = 0;
      
      for (var i = 0; i < this.sliderItemsToShow.length; i++) {
        var slide = this.sliderItemsToShow[i];
        var slideLeft = slide.offsetLeft;
        var distance = Math.abs(slideLeft - scrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }
      
      this.currentPage = closestIndex + 1;
    } else {
      // Desktop calculation
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemWidth) + 1;
    }
    
    // Ensure current page is within bounds
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));

    if (this.prevButton) {
      this.prevButton.removeAttribute('disabled');
      if (this.currentPage == 1 || this.sliderItemsToShow.length <= this.slidesPerPage) {
        this.prevButton.setAttribute('disabled', 'disabled');
      }
    }
    
    if (this.nextButton) {
      this.nextButton.removeAttribute('disabled');
      if (this.currentPage == this.totalPages || this.sliderItemsToShow.length <= this.slidesPerPage) {
        this.nextButton.setAttribute('disabled', 'disabled');
      }
    }

    // Only show navigation arrows on desktop and when needed
    if (this.sliderItemsToShow.length <= this.slidesPerPage || this.totalPages == 1 || this.isMobile) {
      if (this.prevButton) this.prevButton.style.visibility = 'hidden';
      if (this.nextButton) this.nextButton.style.visibility = 'hidden';
    } else {
      if (this.prevButton) this.prevButton.style.visibility = 'visible';
      if (this.nextButton) this.nextButton.style.visibility = 'visible';
    }

    // Update pagination bullets
    if (this.pagination && this.pagination.children.length > 0) {
      for (var i = 0; i < this.pagination.children.length; i++) {
        this.pagination.children[i].classList.remove('active');
      }
      
      var activeIndex = Math.min(this.currentPage - 1, this.pagination.children.length - 1);
      if (activeIndex >= 0) {
        this.pagination.children[activeIndex].classList.add('active');
      }
    }

    // Announce to screen readers (only if pagination exists)
    if (this.pagination) {
      this.announceSlideChange();
    }
  }

  announceSlideChange() {
    // Check if we need to create a live region for screen readers
    // Only create it if pagination exists (implying navigation is needed)
    if (!this.pagination) {
      return; // Skip if pagination doesn't exist
    }
    
    // Create or use existing live region for screen reader announcements
    var liveRegion = this.querySelector('.carousel-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'carousel-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      liveRegion.style.clip = 'rect(0, 0, 0, 0)';
      this.appendChild(liveRegion);
    }
    
    liveRegion.textContent = 'Slide ' + this.currentPage + ' of ' + this.totalPages;
  }

  setActiveSlide(page) {
    // Cancel any existing animation
    if (this.currentAnimation) {
      cancelAnimationFrame(this.currentAnimation);
    }
    
    // Different behavior for mobile vs desktop
    if (this.isMobile) {
      // On mobile, scroll to the element directly to respect CSS scroll-snap-align
      if (this.sliderItemsToShow[page]) {
        this.sliderItemsToShow[page].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    } else {
      // On desktop, use our custom animation
      this.slideScrollPosition = page * this.sliderItemWidth;
      this.smoothScrollTo(this.slideScrollPosition);
    }
  }

  smoothScrollTo(targetPosition) {
    var self = this;
    var startPosition = this.slider.scrollLeft;
    var distance = targetPosition - startPosition;
    var duration = 300; // ms
    var startTime = null;
    
    function animateScroll(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsedTime = timestamp - startTime;
      var progress = Math.min(elapsedTime / duration, 1);
      var easeProgress = self.easeInOutCubic(progress);
      
      self.slider.scrollLeft = startPosition + distance * easeProgress;
      
      if (progress < 1) {
        self.currentAnimation = requestAnimationFrame(animateScroll);
      }
    }
    
    this.currentAnimation = requestAnimationFrame(animateScroll);
  }
  
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  onButtonClick(event) {
    event.preventDefault();
    var step = parseInt(event.currentTarget.dataset.step) || 1;
    var direction = event.currentTarget.name === 'next' ? 1 : -1;
    
    this.slideScrollPosition = this.slider.scrollLeft + (direction * step * this.sliderItemWidth);
    this.smoothScrollTo(this.slideScrollPosition);
    
    // Restart auto-play timer if enabled
    if (this.autoPlayInterval) {
      this.stopAutoPlay();
      this.startAutoPlay();
    }
  }

  onDragStart(event) {
    // Don't interrupt native scrolling on mobile
    if (this.isMobile) return;
    
    this.isDragging = true;
    this.slider.classList.add('dragging');
    
    // Stop any ongoing scroll animation
    if (this.currentAnimation) {
      cancelAnimationFrame(this.currentAnimation);
    }
    
    // Stop auto-play during drag
    this.stopAutoPlay();
    
    // Get initial position
    this.startPosition = this.getPositionX(event);
    this.startScrollLeft = this.slider.scrollLeft;
    
    // Change cursor style
    this.slider.style.cursor = 'grabbing';
  }

  onDragMove(event) {
    if (!this.isDragging || this.isMobile) return;
    
    // Prevent page scrolling
    event.preventDefault();
    
    var currentPosition = this.getPositionX(event);
    var scrollOffset = currentPosition - this.startPosition;
    this.slider.scrollLeft = this.startScrollLeft - scrollOffset;
  }

  onDragEnd(event) {
    if (!this.isDragging || this.isMobile) return;
    
    this.isDragging = false;
    this.slider.classList.remove('dragging');
    
    // Reset cursor style
    this.slider.style.cursor = 'grab';
    
    // Snap to nearest slide
    var scrollPosition = this.slider.scrollLeft;
    var nearestPage = Math.round(scrollPosition / this.sliderItemWidth);
    this.setActiveSlide(nearestPage);
    
    // Restart auto-play if enabled
    if (this.hasAttribute('auto-play')) {
      this.startAutoPlay();
    }
  }
  
  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }
  
  onKeyDown(event) {
    var self = this;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.navigateWithKeyboard(-1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.navigateWithKeyboard(1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActiveSlide(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveSlide(this.totalPages - 1);
        break;
    }
  }
  
  navigateWithKeyboard(direction) {
    // Calculate current page based on scroll position
    var currentPage = Math.round(this.slider.scrollLeft / this.sliderItemWidth);
    var targetPage = Math.max(0, Math.min(currentPage + direction, this.totalPages - 1));
    this.setActiveSlide(targetPage);
    
    // Restart auto-play timer if enabled
    if (this.autoPlayInterval) {
      this.stopAutoPlay();
      this.startAutoPlay();
    }
  }
  
  startAutoPlay() {
    var self = this;
    
    if (this.hasAttribute('auto-play') && !this.autoPlayInterval) {
      this.autoPlayInterval = setInterval(function() {
        // If we're at the last slide, go back to the first
        if (self.currentPage >= self.totalPages) {
          self.setActiveSlide(0);
        } else {
          // Otherwise, go to the next slide
          self.setActiveSlide(self.currentPage);
        }
      }, this.autoPlayDelay);
    }
  }
  
  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }
}

customElements.define('carousel-component', CarouselComponent);